import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  Box,
  Boxes,
  Download,
  Droplet,
  Hash,
  Loader2,
  MapPin,
  Package,
  QrCode,
  Search,
  X,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { toast } from "~/hooks/use-toast";
import { usePagination } from "~/hooks/usePagination";
import {
  type UnifiedSearchResult,
  type UnifiedSearchResultType,
  useLotTraceability,
  usePalletMovementHistory,
  usePositionStatus,
  useProductStockDetail,
  useProductStockList,
  useProductStockMovements,
  useUnifiedStockSearch,
} from "~/hooks/wms";
import { exportStockConsultationToExcel } from "~/services/wms/stockConsultationExportService";

import {
  IngredientDetailView,
  LotDetailView,
  PalletDetailView,
  PositionDetailView,
  ProductStockDetail,
} from "./components/stock-consultation/DetailViews";

const TYPE_CONFIG: Record<
  UnifiedSearchResultType,
  { label: string; icon: React.ElementType; color: string }
> = {
  product: {
    label: "Producto",
    icon: Package,
    color: "bg-blue-100 text-blue-700",
  },
  pallet: {
    label: "Pallet",
    icon: QrCode,
    color: "bg-purple-100 text-purple-700",
  },
  lot: { label: "Lote", icon: Hash, color: "bg-amber-100 text-amber-700" },
  position: {
    label: "Posición",
    icon: MapPin,
    color: "bg-green-100 text-green-700",
  },
  oil: {
    label: "Aceite",
    icon: Droplet,
    color: "bg-yellow-100 text-yellow-700",
  },
  raw_material: {
    label: "Insumo",
    icon: Box,
    color: "bg-orange-100 text-orange-700",
  },
};

// Insumos/products carry a group ("grupo") in extra_data; surface it so classification
// is visible in search results and the detail header.
function getGroupLabel(result: UnifiedSearchResult): string | null {
  const group = (
    result.originalData as { group_description?: string | null } | null
  )?.group_description;
  return group && group.trim() ? group.trim() : null;
}

export function StockConsultationPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedResult, setSelectedResult] =
    useState<UnifiedSearchResult | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportStockConsultationToExcel();
    } catch (error) {
      toast({
        title: "Error al exportar",
        description:
          error instanceof Error
            ? error.message
            : "No se pudo exportar la consulta de stock.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Debounce search input and clear selection when input is cleared
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length >= 2) {
        setDebouncedSearch(searchInput);
      } else {
        setDebouncedSearch("");
        // Clear selection when input is cleared
        if (searchInput.length === 0) {
          setSelectedResult(null);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { results, isLoading, isEmpty } =
    useUnifiedStockSearch(debouncedSearch);

  const handleSelectResult = (result: UnifiedSearchResult) => {
    setSelectedResult(result);
    setDebouncedSearch("");
  };

  const handleClearSelection = () => {
    setSelectedResult(null);
    setSearchInput("");
  };

  return (
    <PageContainer>
      <PageHeader
        title="Consulta de Stock"
        description="Busca por producto, pallet, lote, posición o ingrediente"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Exportar Excel
          </Button>
        }
      />

      {/* Search Input */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por SKU, nombre, código de barras, lote, posición..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              className="pl-9 pr-9"
            />
            {searchInput && !isLoading && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {debouncedSearch && !selectedResult && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Resultados de búsqueda ({results.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : isEmpty ? (
              <div className="py-8 text-center text-muted-foreground">
                <Boxes className="mx-auto h-12 w-12 opacity-30" />
                <p className="mt-2">
                  No se encontraron resultados para "{debouncedSearch}"
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {results.slice(0, 20).map((result) => {
                  const config = TYPE_CONFIG[result.type];
                  const Icon = config.icon;
                  return (
                    <button
                      type="button"
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleSelectResult(result)}
                      className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50"
                    >
                      <div className={`rounded-lg p-2 ${config.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium">
                            {result.name}
                          </span>
                          <Badge variant="outline" className="shrink-0 text-xs">
                            {config.label}
                          </Badge>
                          {getGroupLabel(result) && (
                            <Badge
                              variant="secondary"
                              className="shrink-0 text-xs"
                            >
                              {getGroupLabel(result)}
                            </Badge>
                          )}
                        </div>
                        <p className="truncate text-sm text-muted-foreground">
                          {result.subtitle}
                        </p>
                      </div>
                      {result.code && (
                        <Badge
                          variant="secondary"
                          className="shrink-0 font-mono"
                        >
                          {result.code}
                        </Badge>
                      )}
                    </button>
                  );
                })}
                {results.length > 20 && (
                  <p className="pt-2 text-center text-sm text-muted-foreground">
                    Mostrando 20 de {results.length} resultados. Refina tu
                    búsqueda.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Default view: products ranked by stock */}
      {!debouncedSearch && !selectedResult && (
        <ProductStockRanking onSelect={handleSelectResult} />
      )}

      {/* Selected Result Detail View */}
      {selectedResult && (
        <SelectedResultDetail
          result={selectedResult}
          onClear={handleClearSelection}
        />
      )}
    </PageContainer>
  );
}

interface ProductStockRankingProps {
  onSelect: (result: UnifiedSearchResult) => void;
}

interface ProductStockRankingRow {
  productId: string;
  name: string;
  sku: string | null;
  unit: string | null;
  stock: number;
}

const formatBoxes = (value: number) =>
  new Intl.NumberFormat("es-CL").format(value);

function ProductStockRanking({ onSelect }: ProductStockRankingProps) {
  // products.stock is the live finished-goods balance (production increments it,
  // factura emission decrements it) — the source of truth since the WMS position
  // workflow was retired.
  const { data: rows = [], isLoading } = useProductStockList();

  const products = useMemo<ProductStockRankingRow[]>(
    () =>
      rows.map((r) => ({
        productId: r.product_id,
        name: r.name,
        sku: r.sku,
        unit: r.unit,
        stock: r.stock,
      })),
    [rows]
  );

  const PAGE_SIZE = 10;
  const { page, setPage, pageCount, pageItems, total } = usePagination(
    products,
    PAGE_SIZE
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Stock de Productos Terminados
        </CardTitle>
        <CardDescription>
          Saldo actual por producto, de mayor a menor stock
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={10} columns={4} />
          </div>
        ) : products.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={Boxes}
              title="Sin productos"
              description="No hay productos terminados registrados"
            />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">SKU</TableHead>
                  <TableHead>Producto</TableHead>
                  <TableHead className="whitespace-nowrap">Unidad</TableHead>
                  <TableHead className="whitespace-nowrap text-right">
                    Stock
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((product, index) => (
                  <TableRow
                    key={product.productId}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/40"
                    onClick={() =>
                      onSelect({
                        id: product.productId,
                        type: "product",
                        name: product.name,
                        code: product.sku,
                        subtitle: "",
                        originalData: null,
                      })
                    }
                  >
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {product.sku || "-"}
                    </TableCell>
                    <TableCell className="font-medium">
                      {product.name}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {product.unit || "-"}
                    </TableCell>
                    <TableCell
                      className={`text-right font-mono font-semibold ${
                        product.stock < 0 ? "text-red-700" : ""
                      }`}
                    >
                      {formatBoxes(product.stock)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              page={page}
              pageCount={pageCount}
              totalItems={total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              itemLabel="productos"
              className="border-t px-4 py-3"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface SelectedResultDetailProps {
  result: UnifiedSearchResult;
  onClear: () => void;
}

function SelectedResultDetail({ result, onClear }: SelectedResultDetailProps) {
  const config = TYPE_CONFIG[result.type];
  const Icon = config.icon;

  // Fetch detail data based on type
  const productId = result.type === "product" ? result.id : null;
  const { data: productStock, isLoading: productStockLoading } =
    useProductStockDetail(productId);
  const { data: productMovements = [], isLoading: productMovementsLoading } =
    useProductStockMovements(productId);
  const { data: palletHistory = [], isLoading: historyLoading } =
    usePalletMovementHistory(result.type === "pallet" ? result.id : null);
  const { data: lotData = [] } = useLotTraceability(
    result.type === "lot" ? result.code || "" : ""
  );
  const { data: positionStatus = [], isLoading: positionLoading } =
    usePositionStatus(result.type === "position" ? result.id : null);

  return (
    <div className="space-y-6">
      {/* Selected Item Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`rounded-lg p-3 ${config.color}`}>
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{result.name}</h2>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant="outline">{config.label}</Badge>
                  {getGroupLabel(result) && (
                    <Badge variant="secondary">{getGroupLabel(result)}</Badge>
                  )}
                  {result.code && (
                    <span className="font-mono text-sm text-muted-foreground">
                      {result.code}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 shrink-0 text-muted-foreground"
              onClick={onClear}
            >
              <X className="mr-2 h-4 w-4" />
              Cerrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Detail View based on type */}
      {result.type === "product" && (
        <ProductStockDetail
          detail={productStock}
          movements={productMovements}
          isLoading={productStockLoading || productMovementsLoading}
        />
      )}

      {result.type === "pallet" && (
        <PalletDetailView
          pallet={
            result.originalData as import("~/types/wms").PalletSearchResult
          }
          movementHistory={palletHistory}
          isLoading={historyLoading}
        />
      )}

      {result.type === "lot" && (
        <LotDetailView lotNumber={result.code || ""} lotData={lotData} />
      )}

      {result.type === "position" && (
        <PositionDetailView
          positionStatus={positionStatus}
          isLoading={positionLoading}
        />
      )}

      {(result.type === "oil" || result.type === "raw_material") && (
        <IngredientDetailView type={result.type} data={result.originalData} />
      )}
    </div>
  );
}

export default StockConsultationPage;
